import type { CustomFieldDefinition, CustomFieldValueWithDefinition } from '@vy/custom-fields-types';

const VAYU_SOURCE_LABEL = 'Vayu';

const resolveSourceSortKey = (definition: CustomFieldDefinition | undefined): string =>
  definition?.integrationSource ?? VAYU_SOURCE_LABEL;

const resolveNameSortKey = (row: CustomFieldValueWithDefinition): string =>
  row.definition?.vayuCustomFieldName ?? row.fieldName ?? '';

const mergeCustomFieldRows = (
  valuesWithDefinitions: CustomFieldValueWithDefinition[],
  allDefinitions: CustomFieldDefinition[],
): CustomFieldValueWithDefinition[] => {
  const valuesByDefinitionId = new Map<string, CustomFieldValueWithDefinition>();
  valuesWithDefinitions.forEach((row) => {
    valuesByDefinitionId.set(row.customFieldDefinitionId, row);
  });

  const merged: CustomFieldValueWithDefinition[] = [];

  allDefinitions.forEach((definition) => {
    const existing = valuesByDefinitionId.get(definition.id);

    if (existing) {
      merged.push(existing);
      valuesByDefinitionId.delete(definition.id);
      return;
    }

    merged.push({
      customFieldDefinitionId: definition.id,
      fieldName: definition.vayuCustomFieldName,
      value: null,
      definition,
    });
  });

  valuesByDefinitionId.forEach((orphanValue) => {
    merged.push(orphanValue);
  });

  return merged;
};

const sortCustomFieldRows = (rows: CustomFieldValueWithDefinition[]): CustomFieldValueWithDefinition[] =>
  [...rows].sort((customFieldRowA, customFieldRowB) => {
    const resolvedSourceCustomFieldA = resolveSourceSortKey(customFieldRowA.definition);
    const resolvedSourceCustomFieldB = resolveSourceSortKey(customFieldRowB.definition);
    const sourceCompare = resolvedSourceCustomFieldA.localeCompare(resolvedSourceCustomFieldB);

    if (sourceCompare !== 0) {
      return sourceCompare;
    }

    const resolvedNameCustomFieldA = resolveNameSortKey(customFieldRowA);
    const resolvedNameCustomFieldB = resolveNameSortKey(customFieldRowB);

    return resolvedNameCustomFieldA.localeCompare(resolvedNameCustomFieldB);
  });

export const buildCustomFieldRows = (
  valuesWithDefinitions: CustomFieldValueWithDefinition[] = [],
  allDefinitions: CustomFieldDefinition[] = [],
): CustomFieldValueWithDefinition[] => {
  const mergedCustomFieldRows = mergeCustomFieldRows(valuesWithDefinitions, allDefinitions);

  return sortCustomFieldRows(mergedCustomFieldRows);
};
